import { ReactNode, useState } from "react";
import clsx from "clsx";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import { Button } from "@/components/UI/Button";
import useTableUIStore from "@/store/tableUIStore";
import featureFlagStore from "@/store/featureFlagStore";
import {
  IRequestInformationFields,
  requestExecutionRequestInformation,
} from "@/store/tradeSettingsStoreACTIONS";
import requestInformationStore from "@/store/requestInformationStore";
import { RequestInfoStatus } from "@/lib/interfaces and Enums/ENUMRequestInformationStatus";
import { QuoteSide } from "@/lib/interfaces and Enums/ENUMQuoteSide";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { statusFormExecutedToAffirmedRange } from "@/components/widgets/tradeActionSummary/helper/colDefs/colDefsShared";
import {
  getFontSizeClass,
  TRADE_SETTINGS_CONTROL_HEIGHT,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import {
  formatCoverValueForDisplay,
  hasCoverValue,
  resolveCoverNotation,
} from "@/components/widgets/tradeActionSummary/helper/cellRenderers/CoverCellRenderer/coverFormatter";

// LQC-1301 — RP-perspective Trade Settings view. Unlike the OP panel (which
// edits settings via the Edit/Settings flow), the RP only VIEWS what the OP
// advised. The values are read straight off the blotter trade data — the same
// fields the RP-side blotter columns bind to (cover / coverSpread /
// competition / quoteType) — so this view is read-only with no hydration.
//
// LQC-658 — adds a Direction row plus a "Request Information" action: when the
// trade is Two-Way the actual Buy/Sell direction is withheld until the OP
// releases it, so the RP gets a button to request it.

const DASH = "—";

// Pure: the RP perspective has a single execution request, so its id lives on
// the trade root (fallback: the first execution-request entry). Always sent with
// a request-information POST. Exported for unit testing.
export function resolveRpExecutionRequestId(
  trade: any,
): number | string | undefined {
  return (
    trade?.executionRequestId ??
    trade?.executionRequestCounterParties?.[0]?.executionRequestId
  );
}

// LQC-658: a Two-Way quote withholds the actual Bid/Ask side until the OP
// releases it, so the RP can request it. Once the side is shown (Bid or Ask)
// there's nothing to request — quoteSideER holds Bid / Ask / Two-Way (the same
// field the RP blotter row colouring binds to).
export function canRequestDirection(trade: any): boolean {
  return trade?.quoteSideER === QuoteSide.TwoWay;
}

// Pure: the RP sees the direction as a status, not the raw side — "Withheld"
// while the OP is quoting Two-Way, otherwise "Shown".
export function resolveRpDirectionDisplay(trade: any): "Withheld" | "Shown" {
  return canRequestDirection(trade) ? "Withheld" : "Shown";
}

// Which IRequestInformationFields field a row maps to.
type RequestField = keyof IRequestInformationFields;

// LQC-637: colour for the status word shown beside the Request button. Requested
// shows in the default colour (the RP is waiting on the OP — amber is reserved
// for "your turn to respond"); Rejected (orange, per LQC-1830) / Released
// (green); False shows no word so no colour. Exported for unit testing.
export function getRequestStatusColourClass(status: RequestInfoStatus): string {
  switch (status) {
    case RequestInfoStatus.Rejected:
      return "text-orange-500";
    case RequestInfoStatus.True:
      return "text-green-500";
    default:
      return "";
  }
}

// Pure: does the trade carry a displayable value for this field (vs. a dash)?
export function hasDisplayValue(v: unknown): boolean {
  return v !== undefined && v !== null && v !== "";
}

// True once a CP is Executed (or any later status — the executed-change states /
// Affirmed): the Cover and Cover Spread values now exist and can be requested.
// At Awarded they do NOT exist yet, so the trade is NOT yet requestable even
// though it is past the quoting phase. Used by Cover / Cover Spread
// requestability below.
export function isExecutedOnwards(trade: any): boolean {
  return statusFormExecutedToAffirmedRange.includes(trade?.status);
}

// DealtAway / NotDealt — the RP did NOT win the trade, but it has resolved, so
// the Cover / Cover Spread exist and can be requested.
export function isDealtAwayOrNotDealt(trade: any): boolean {
  return (
    trade?.status === ExecutionStrategyStatus.DealtAway ||
    trade?.status === ExecutionStrategyStatus.NotDealt
  );
}

// LQC-658: Cover / Cover Spread can be requested once the trade has reached
// Executed (or any later status — executed-change states / Affirmed), reusing
// the shared `statusFormExecutedToAffirmedRange` group. Crucially NOT at Awarded:
// the Cover / Cover Spread don't exist yet at Awarded, so requesting them is
// impossible. DealtAway / NotDealt are also allowed (the trade resolved away
// from the RP, but the values still exist).
//
// TODO (LQC-658): DealtAway / NotDealt need a caveat. They mean the RP was NOT
// awarded the trade — so they should only be able to request cover / cover
// spread if they actually INTERACTED with it by quoting (had a bid or ask on the
// trade's blocks). A trade merely sent to them that went DealtAway / NotDealt
// with no quote should NOT be requestable. Detecting genuine quote interaction is
// non-trivial, so for now they're allowed with NO caveat — revisit to add the
// "did they quote?" check.
export function canRequestCoverInfo(trade: any): boolean {
  return isExecutedOnwards(trade) || isDealtAwayOrNotDealt(trade);
}

// Stable per-row key. Drives both the display row and its central request rule.
export type RpRowKey = "side" | "direction" | "comp" | "cover" | "coverSpread";

// LQC-658: ONE central place that defines, per request button, when it can be
// actioned vs. disabled and the tooltip explaining why it's disabled. Each
// button in the view reads its behaviour from here — to change a rule (or the
// disabled message) you only touch this object.
//
// `disabledTooltip(trade)` is a function so a single button can explain DIFFERENT
// disabled reasons (e.g. Side: "already one-way" vs. "trade past quoting").
//
// `canRequest(trade)` may key off the quote type, the value already showing, the
// trade status, or any combination. `cover` / `coverSpread` are still
// PLACEHOLDERS (always disabled) until their real gating is defined.
export interface IRpRequestRule {
  // The info field POSTed to the request endpoint when the button is actioned.
  // undefined → no endpoint field wired yet for this row.
  field?: RequestField;
  // When the button is actionable for the given trade.
  canRequest: (trade: any) => boolean;
  // Tooltip shown while the button is disabled — derived from the reason it
  // can't currently be actioned.
  disabledTooltip: (trade: any) => string;
}

export const RP_REQUEST_RULES: Record<RpRowKey, IRpRequestRule> = {
  // Side is intentionally NOT requestable by the RP: it duplicated the Direction
  // row (both POSTed `direction`). The row still DISPLAYS the quote type, but
  // carries no Request button — no `field`, so the button block is skipped. The
  // rule is kept so the row → rule lookup stays total.
  side: {
    field: undefined,
    canRequest: () => false,
    disabledTooltip: () => "",
  },
  // Direction shows "Withheld" or "Shown"; requestable only while Withheld.
  direction: {
    field: "direction",
    canRequest: (trade) => resolveRpDirectionDisplay(trade) === "Withheld",
    disabledTooltip: () => "Direction already shown — nothing to request.",
  },
  // Comp can be requested at ANY status — gated only on whether the competition
  // value is already showing. If it's showing there's nothing to request.
  comp: {
    field: "competition",
    canRequest: (trade) => !hasDisplayValue(trade?.competition),
    disabledTooltip: () => "Comp already showing — nothing to request.",
  },
  // Cover / Cover Spread are status driven: requestable once the trade is
  // Executed onwards, or DealtAway (see canRequestCoverInfo TODO re: the
  // DealtAway quote-interaction caveat). NOT at Awarded — the values don't
  // exist yet at that stage. ALSO not requestable once the value is already
  // showing — the OP may release Cover up-front (e.g. set to "Yes" from New),
  // in which case there's no negotiation status to block the button, so we
  // must guard on the value being present here (mirrors the Comp rule).
  cover: {
    field: "cover",
    canRequest: (trade) =>
      canRequestCoverInfo(trade) && !hasCoverValue(trade?.cover),
    disabledTooltip: (trade) =>
      hasCoverValue(trade?.cover)
        ? "Cover already showing — nothing to request."
        : "Cover can be requested once the trade is executed.",
  },
  coverSpread: {
    field: "coverSpread",
    canRequest: (trade) =>
      canRequestCoverInfo(trade) && !hasCoverValue(trade?.coverSpread),
    disabledTooltip: (trade) =>
      hasCoverValue(trade?.coverSpread)
        ? "Cover Spread already showing — nothing to request."
        : "Cover Spread can be requested once the trade is executed.",
  },
};

// LQC-637 — a field is requestable only when the trade-data rule allows it AND
// the RP isn't already waiting on it (Requested) or already been granted it
// (True). A prior Rejected does NOT block — the RP may ask again. This is the
// guard the trade-data rules alone can't provide: e.g. Cover / Cover Spread are
// gated purely on status (Awarded onwards), so without this they'd stay
// requestable even after release. Display-only rows (no field, e.g. Side) are
// never requestable. Exported for unit testing.
export function isFieldRequestable(
  rule: IRpRequestRule,
  trade: any,
  status: RequestInfoStatus,
): boolean {
  if (rule.field === undefined) return false;
  if (
    status === RequestInfoStatus.Requested ||
    status === RequestInfoStatus.True
  ) {
    return false;
  }
  return rule.canRequest(trade);
}

// LQC-637 — disabled-state tooltip. A negotiation-status block (already
// requested / already released) takes precedence over the trade-data reason;
// otherwise defer to the rule's own message. Exported for unit testing.
export function requestDisabledTooltip(
  rule: IRpRequestRule,
  trade: any,
  status: RequestInfoStatus,
): string {
  if (status === RequestInfoStatus.Requested)
    return "Already requested — waiting on counterparty to respond.";
  if (status === RequestInfoStatus.True) return "Already released.";
  return rule.disabledTooltip(trade);
}

// LQC-637 — the negotiation status is shown IN the button, not beside it. This
// object holds the per-status styling rules; update it to retune a state. False
// (no request yet) is the actionable "Request" state and keeps the app's existing
// <Button> design, so it isn't here — the renderer falls back to <Button> for it.
//   • Requested — app blue (#5DA2D2) border + circle, label stays normal colour.
//   • Released  — green tick, fully green (per design spec).
//   • Rejected  — orange cross, fully orange (LQC-1830: orange, not red).
export interface IRequestStatusStyle {
  label: string;
  icon: string; // leading glyph
  borderClass: string; // button border colour
  iconClass: string; // leading glyph colour
  textClass: string; // label colour ("" = inherit the panel's normal colour)
}

export const REQUEST_STATUS_STYLES: Partial<
  Record<RequestInfoStatus, IRequestStatusStyle>
> = {
  [RequestInfoStatus.Requested]: {
    label: "Requested",
    icon: "○",
    borderClass: "border-[#5DA2D2]",
    iconClass: "text-[#5DA2D2]",
    textClass: "",
  },
  [RequestInfoStatus.True]: {
    label: "Released",
    icon: "✓",
    borderClass: "border-green-500",
    iconClass: "text-green-600 dark:text-green-400",
    textClass: "text-green-600 dark:text-green-400",
  },
  [RequestInfoStatus.Rejected]: {
    label: "Rejected",
    icon: "✕",
    borderClass: "border-orange-500",
    iconClass: "text-orange-600 dark:text-orange-400",
    textClass: "text-orange-600 dark:text-orange-400",
  },
};

export interface IRpSettingRow {
  key: RpRowKey;
  heading: string;
  value: ReactNode;
}

// Pure: pull the RP-advised display values off a blotter trade. Exported for
// unit testing. Mirrors the RP execution-request column bindings.
//
// LQC-1709: Cover / Cover Spread are notation-aware values, so they go through
// the SAME formatter the RP blotter columns use (CoverCellRenderer) rather than
// a bare String() — this applies the right suffix/symbol and resolves the
// outright-vs-structure notation split (leg notation vs notationER) via
// resolveCoverNotation. Side / Comp / Direction are plain text and need no
// notation.
export function buildRpSettingsRows(trade: any): IRpSettingRow[] {
  const show = (v: unknown) => (hasDisplayValue(v) ? String(v) : DASH);
  const showCover = (value: unknown) =>
    hasCoverValue(value)
      ? formatCoverValueForDisplay(value, resolveCoverNotation(trade), trade)
      : DASH;
  return [
    { key: "side", heading: "Side", value: show(trade?.quoteType) },
    {
      key: "direction",
      heading: "Direction",
      value: resolveRpDirectionDisplay(trade),
    },
    { key: "comp", heading: "Comp", value: show(trade?.competition) },
    { key: "cover", heading: "Cover", value: showCover(trade?.cover) },
    {
      key: "coverSpread",
      heading: "Cover Spread",
      value: showCover(trade?.coverSpread),
    },
  ];
}

const TradeSettingsRpView = ({ trade }: { trade: any }) => {
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const fontSizeClass = getFontSizeClass(fontSizeSetting);
  const { getAccessTokenSilently } = useAuth0();
  // LQC-658: feature-flagged. When off, no Request buttons render at all — the
  // request endpoint is never reachable from the UI.
  const requestButtonsEnabled = featureFlagStore(
    (state) => state.rpRequestInformationButtons,
  );
  // Which field's request is in flight (one POST at a time per row).
  const [requestingField, setRequestingField] = useState<RequestField | null>(
    null,
  );

  // LQC-637: subscribe to this trade's single ER request-info slice so the status
  // word beside each button stays in sync (optimistic Requested on click, then the
  // authoritative True/Rejected from the SignalR push).
  const erId = resolveRpExecutionRequestId(trade);
  const requestInfoForEr = requestInformationStore((state) =>
    trade?.esid && erId !== undefined
      ? state.requestInfo[trade.esid]?.[String(erId)]
      : undefined,
  );

  const rows = buildRpSettingsRows(trade);

  const handleRequest = async (field: RequestField) => {
    const executionRequestId = resolveRpExecutionRequestId(trade);
    if (!trade?.esid || executionRequestId === undefined) return;
    setRequestingField(field);
    try {
      await requestExecutionRequestInformation({
        esid: trade.esid,
        executionRequestId,
        fields: { [field]: true },
        getAccessTokenSilently,
      });
      toast.info("Request sent.");
    } finally {
      setRequestingField(null);
    }
  };

  return (
    <div
      data-testid="TradeSettingsRpViewContainer"
      id="TradeSettingsRpViewContainer"
      className={clsx("w-full min-w-0", fontSizeClass)}
    >
      {/* LQC-2998: rows keep their natural width and scroll when the panel narrows, instead of clipping the buttons. */}
      <div className="condensed-grid-scroll">
        <div className="flex w-max min-w-full flex-col gap-2">
          {/* Four flex columns, left → right: heading (grows), the SETTING value
          (fixed width so every value — incl. "Withheld"/"Two-Way" — lines up),
          the Request button column, then the response/status column. The button
          and status columns are reserved on EVERY row (even non-requestable ones
          like Side, where the button is invisible and the status empty) so the
          value column stays aligned across all rows. */}
          {rows.map((row) => {
            // Every button reads its behaviour from the central rules object.
            const rule = RP_REQUEST_RULES[row.key];
            // A row carries a button only if it has a wired field (Side has none —
            // it's display-only, but still reserves the button/status columns).
            const hasRequest = rule.field !== undefined;
            // LQC-637: negotiation status for this row's field (defaults to False —
            // no word). Optimistically Requested on click; reconciled by SignalR.
            const status =
              (rule.field && requestInfoForEr?.[rule.field]) ||
              RequestInfoStatus.False;
            // Trade-data rule AND negotiation status: blocks re-requesting something
            // already pending (Requested) or granted (True); Rejected stays open.
            const requestable = isFieldRequestable(rule, trade, status);
            const disabled = !requestable || requestingField === rule.field;
            // The status is merged INTO the button: False → the actionable "Request"
            // button (existing design); Requested/Released/Rejected → a styled status
            // button driven by REQUEST_STATUS_STYLES.
            const statusStyle = REQUEST_STATUS_STYLES[status];
            return (
              <div
                key={row.key}
                data-testid={`TradeSettingsRpRow-${row.heading}`}
                className="flex items-center gap-3 border-b border-gray-200 pb-2 dark:border-gray-700"
              >
                <span className="flex-1 whitespace-nowrap text-gray-600 dark:text-gray-300">
                  {row.heading}
                </span>
                <span className="w-16 text-right font-medium">{row.value}</span>
                {/* LQC-658/LQC-637: feature-flagged action column. The negotiation
                status is merged INTO the button:
                  • Side (no field) → an invisible spacer reserves the column so
                    the value column above stays aligned.
                  • False (no request yet) → the actionable "Request" button,
                    keeping the app's existing design.
                  • Requested / Released / Rejected → a styled status button from
                    REQUEST_STATUS_STYLES. Released / Requested aren't actionable;
                    Rejected stays clickable (re-request). */}
                {requestButtonsEnabled &&
                  (!hasRequest ? (
                    // Side (display-only): invisible Button reserves the EXACT same
                    // footprint as a real one so the value column stays aligned.
                    <Button
                      type="button"
                      aria-hidden
                      tabIndex={-1}
                      disabled
                      className={clsx(
                        TRADE_SETTINGS_CONTROL_HEIGHT,
                        "invisible w-28 flex-none",
                      )}
                    >
                      Request
                    </Button>
                  ) : statusStyle ? (
                    // Requested / Released / Rejected — SAME footprint as the Request
                    // button (shared height constant + w-28), matching the Advise
                    // dropdowns; only the border / icon / text colour change.
                    <Button
                      type="button"
                      disabled={disabled}
                      title={
                        !requestable
                          ? requestDisabledTooltip(rule, trade, status)
                          : "Request again"
                      }
                      data-testid={`TradeSettingsRpRequestButton-${row.heading}`}
                      id={`TradeSettingsRpRequestButton-${row.heading}`}
                      onClick={() => rule.field && handleRequest(rule.field)}
                      className={clsx(
                        TRADE_SETTINGS_CONTROL_HEIGHT,
                        "w-28 flex-none gap-1 rounded border bg-transparent dark:bg-transparent",
                        statusStyle.borderClass,
                        statusStyle.textClass,
                        disabled
                          ? "cursor-default"
                          : "cursor-pointer hover:opacity-80",
                      )}
                    >
                      <span className={statusStyle.iconClass}>
                        {statusStyle.icon}
                      </span>
                      <span>{statusStyle.label}</span>
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="secondaryBlueActionAtTradeCreation"
                      className={clsx(
                        TRADE_SETTINGS_CONTROL_HEIGHT,
                        "w-28 flex-none",
                        disabled && "cursor-not-allowed opacity-50",
                      )}
                      disabled={disabled}
                      title={
                        !requestable
                          ? requestDisabledTooltip(rule, trade, status)
                          : undefined
                      }
                      data-testid={`TradeSettingsRpRequestButton-${row.heading}`}
                      id={`TradeSettingsRpRequestButton-${row.heading}`}
                      onClick={() => rule.field && handleRequest(rule.field)}
                    >
                      Request
                    </Button>
                  ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TradeSettingsRpView;
